import { ContentType } from 'application-types';
import { getVisitorManagementFeatures, InteractionWithVisitorManagement } from '../../types/visitorFormInteraction';
import { validateVisitorEndTime } from './validateVisitorEndTime';
import { validateVisitorTenant } from './validateVisitorTenant';
import { TFunction } from 'i18next';
import { InviteNameProperties } from 'constants-visitor-management';
import { BlockNode } from '../../types/VisitorInviteName';


/**
 * Recursively checks whether the layout tree contains
 * a PropertyInputBlock bound to VisitorManagement.inviteName
 */
function hasInviteNameProperty(block?: BlockNode): boolean {
  if (!block) return false;

  const isInviteNameBlock =
    block.primitive === InviteNameProperties.primitive &&
    block.properties?.featureKey === InviteNameProperties.featureKey &&
    block.properties?.propertyKey === InviteNameProperties.propertyKey;

  if (isInviteNameBlock) return true;

  const children: BlockNode[] = [
    ...(Array.isArray(block.properties?.children) ? block.properties.children : []),
    ...(Array.isArray(block.children) ? block.children : []),
  ];

  return children.some(hasInviteNameProperty);
}

/**
 * Returns true if the visitor invite form layout
 * contains an Invite Name input field.
 * Returns false when isInviteNameEnabled is false (feature flag off).
 */
export function formHasInviteNameBlockInLayout(
  content: ContentType,
  isInviteNameEnabled?: boolean
): boolean {
  if (isInviteNameEnabled === false) return false;

  return hasInviteNameProperty(content?.block as BlockNode | undefined);
}

/**
 * Returns true when the Invite Name field should be required:
 * invite name feature is enabled, form has Invite Name block, and visitors were added via bulk CSV.
 * Use this to mark the invite name field as required on the fly.
 * Pass isInviteNameEnabled so the result respects the feature flag (consistent with ContentRenderer).
 */
export function isInviteNameRequiredForInteraction(
  content: ContentType,
  interaction: InteractionWithVisitorManagement,
  isInviteNameEnabled?: boolean
): boolean {
  const vm = getVisitorManagementFeatures(interaction);
  const uploadedViaBulkImport = Boolean(vm?.uploadedViaBulkImport);

  return (
    formHasInviteNameBlockInLayout(content, isInviteNameEnabled) &&
    uploadedViaBulkImport
  );
}

export function validateVisitorInteraction(
  content: ContentType,
  interaction: {
    data: {};
    state: {};
    features: {};
  },
  t: TFunction,
  setSubmitAttempted: Function
): boolean {
  const endTimeValid = validateVisitorEndTime(content, interaction);
  const tenantValid = validateVisitorTenant(interaction);

  if (!endTimeValid) {
    window.Toast.show(
      t('web.admin.channel.visitor.passDetails.validationError.futureDateTime'),
      5000
    );
    setSubmitAttempted(false);

    return false;
  }

  if (!tenantValid) {
    window.Toast.show(
      t(
        'web.admin.channel.visitor.passDetails.validationError.missingTenantId'
      ),
      5000
    );
    setSubmitAttempted(false);

    return false;
  }

  return true;
}
